import React, { useEffect, useRef, useState, useCallback } from 'react';
import { socket, joinMediasoupRoom, mediasoupClient } from '../../api/socket';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import {
  Pencil, Trash2, Image as ImageIcon, Send, Highlighter, MousePointer2,
  X, LogOut, Camera, HelpCircle, Minimize2, Maximize2, Scan,
  LayoutDashboard, Search, Users, Wifi, WifiOff, Eraser, Home, Lock as LockIcon, Unlock,
  ChevronLeft, ChevronRight, Plus
} from 'lucide-react';
import DepositModal from '../../components/DepositModal';
import WithdrawModal from './WithdrawModal';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';

interface Job {
  id: string;
  studentId: string;
  studentName: string;
  budget?: number;
  hourlyRate?: number;
  subject: string;
}

const StudentVideo = React.memo(({ stream, volume, onScan, className, t }: { stream: MediaStream, volume: number, onScan?: () => void, className?: string, t: any }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      // Note: Video is muted to bypass browser autoplay policies. Audio is handled by remoteAudioRef.
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) playPromise.catch(e => console.warn("Student video play blocked:", e));
    }
  }, [stream]);

  return (
    <div className="bg-slate-900 rounded-2xl aspect-video overflow-hidden border-2 border-slate-700 relative shadow-2xl group transition-all hover:border-blue-500/50">
      <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${className || ''}`} />
      <div className="absolute bottom-2 left-2 bg-black/50 backdrop-blur-md px-2 py-1 rounded-lg border border-white/10 flex items-center">
        <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse mr-2" />
        <span className="text-[10px] font-bold uppercase text-white/80 tracking-tighter">{t('student_live', 'Student Live')}</span>
      </div>
      {onScan && (
        <button onClick={(e) => { e.stopPropagation(); onScan(); }} title={t('capture_scan', 'Capture & Scan Homework')} className="absolute top-2 right-2 p-2.5 bg-green-600 hover:bg-green-500 text-white rounded-xl shadow-xl border border-green-400/50 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-200 active:scale-90 flex items-center gap-2">
          <Scan size={16} />
          <span className="text-[10px] font-black uppercase tracking-tighter pr-1">{t('capture', 'Capture')}</span>
        </button>
      )}
    </div>
  );
});

const TutorDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [roomId, setRoomId] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [isLowBandwidthMode, setIsLowBandwidthMode] = useState(false);
  const [isStudentDrawingAllowed, setIsStudentDrawingAllowed] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const toggleStudentDrawing = () => {
    const newState = !isStudentDrawingAllowed;
    setIsStudentDrawingAllowed(newState);
    if (socket) {
      socket.emit('toggle-student-drawing', { roomId, allowed: newState });
    }
  };

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(3);
  const [isDrawing, setIsDrawing] = useState(false);

  const [tool, setTool] = useState<'pen' | 'highlighter' | 'laser' | 'eraser'>('pen');
  const [laserPos, setLaserPos] = useState({ x: 0, y: 0, active: false });
  const lastXRef = useRef<number | null>(null);
  const lastYRef = useRef<number | null>(null);

  const strokesRef = useRef<any[]>([]);

  const [messages, setMessages] = useState<{ sender: string, text: string, type: string }[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [quizData, setQuizData] = useState({ type: 'mcq' as 'mcq' | 'text', question: '', options: ['', '', '', ''] });

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const [studentStreams, setStudentStreams] = useState<{ id: string, stream: MediaStream, kind: string, userId: string }[]>([]);

  const deviceRef = useRef<any>(null);
  const recvTransportRef = useRef<any>(null);
  const sendTransportRef = useRef<any>(null);
  const isStreaming = useRef(false);
  const localStreamRef = useRef<MediaStream | null>(null);
  const isInitializing = useRef(false);
  const renderMapRef = useRef<((img: HTMLImageElement) => void) | null>(null);
  const mediaSoupActive = useRef(false);
  const lastImgRef = useRef<HTMLImageElement | null>(null);
  const lastProcessedMessageRef = useRef<string>('');
  const consumedProducersRef = useRef<Set<string>>(new Set());
  const videoProducerRef = useRef<any>(null);
  const [isLocalStreaming, setIsLocalStreaming] = useState(false);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);

  const roomIdRef = useRef(roomId);
  useEffect(() => { roomIdRef.current = roomId; }, [roomId]);

  // Clean up WebRTC Transports on Unmount
  useEffect(() => {
    return () => {
      if (videoProducerRef.current) videoProducerRef.current.close();
      if (sendTransportRef.current) sendTransportRef.current.close();
      if (recvTransportRef.current) recvTransportRef.current.close();
    };
  }, []);

  useEffect(() => {
    const fetchJobs = async () => {
      try { const res = await api.get('/tutor/jobs/accepted'); setJobs(res.data); }
      catch (err) { } finally { setLoadingJobs(false); }
    };
    fetchJobs();
  }, []);

  const renderMap = useCallback((img: HTMLImageElement) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    if (!ctx) return;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const logicalWidth = canvas.width / dpr;
    const logicalHeight = canvas.height / dpr;

    const ratio = Math.min(logicalWidth / img.width, logicalHeight / img.height);
    const offsetX = (logicalWidth - img.width * ratio) / 2;
    const offsetY = (logicalHeight - img.height * ratio) / 2;

    ctx.scale(dpr, dpr);
    ctx.drawImage(img, offsetX, offsetY, img.width * ratio, img.height * ratio);

    let rx: number | null = null, ry: number | null = null;
    strokesRef.current.forEach(pt => {
      const px = pt.x * logicalWidth;
      const py = pt.y * logicalHeight;
      ctx.globalAlpha = pt.alpha || 1;
      ctx.strokeStyle = pt.color;
      ctx.lineWidth = pt.width;
      if (!pt.isDragging) {
        ctx.beginPath();
        ctx.moveTo(px, py);
        rx = px; ry = py;
      } else if (rx !== null && ry !== null) {
        const midX = (rx + px) / 2;
        const midY = (ry + py) / 2;

        ctx.quadraticCurveTo(rx, ry, midX, midY);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(midX, midY);
        rx = px; ry = py;
      }
    });

    renderMapRef.current = renderMap;
  }, []);

  useEffect(() => {
    renderMapRef.current = renderMap;
  }, [renderMap]);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();

    if (rect.width === 0 || rect.height === 0) return;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    if (lastImgRef.current) {
      const img = lastImgRef.current;
      const ratio = Math.min(rect.width / img.width, rect.height / img.height);
      const offsetX = (rect.width - img.width * ratio) / 2;
      const offsetY = (rect.height - img.height * ratio) / 2;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, offsetX, offsetY, img.width * ratio, img.height * ratio);
    }

    let rx: number | null = null;
    let ry: number | null = null;

    strokesRef.current.forEach(pt => {
      const px = pt.x * rect.width;
      const py = pt.y * rect.height;
      ctx.globalAlpha = pt.alpha || 1;
      ctx.strokeStyle = pt.color;
      ctx.lineWidth = pt.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (!pt.isDragging) {
        ctx.beginPath();
        ctx.moveTo(px, py);
        rx = px; ry = py;
      } else {
        if (rx !== null && ry !== null) {
          const midX = (rx + px) / 2;
          const midY = (ry + py) / 2;

          ctx.quadraticCurveTo(rx, ry, midX, midY);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(midX, midY);
        } else {
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(px, py);
          ctx.stroke();
        }
        rx = px; ry = py;
      }
    });

  }, [isFocusMode]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => { setupCanvas(); });
    observer.observe(container);
    return () => observer.disconnect();
  }, [setupCanvas]);

  useEffect(() => {
    if (!socket || !roomId) return;
    if (isLowBandwidthMode) {
      if (videoProducerRef.current && !videoProducerRef.current.paused) {
        videoProducerRef.current.pause();
        socket.emit('pause-producer', { roomId, producerId: videoProducerRef.current.id });
      }
      socket.emit('apply-bandwidth-limit', { roomId, mode: 'low' });
    } else {
      if (videoProducerRef.current && videoProducerRef.current.paused && isLocalStreaming) {
        videoProducerRef.current.resume();
        socket.emit('resume-producer', { roomId, producerId: videoProducerRef.current.id });
      }
      socket.emit('apply-bandwidth-limit', { roomId, mode: 'high' });
    }
  }, [isLowBandwidthMode, roomId]);

  const consumeStudentStream = useCallback(async (
    producerId: string,
    kind: string,
    studentSocketId: string,
    appData: any,
    manualRoomId?: string
  ) => {
    const currentRoomId = manualRoomId || roomIdRef.current;
    if (consumedProducersRef.current.has(producerId)) return;
    consumedProducersRef.current.add(producerId);

    if (!recvTransportRef.current || !deviceRef.current || !currentRoomId) {
      consumedProducersRef.current.delete(producerId);
      setTimeout(() => consumeStudentStream(producerId, kind, studentSocketId, appData, currentRoomId), 500);
      return;
    }

    const rtpCapabilities = deviceRef.current.rtpCapabilities;
    if (!rtpCapabilities) {
      consumedProducersRef.current.delete(producerId);
      return;
    }

    socket.emit('consume', {
      roomId: currentRoomId,
      transportId: recvTransportRef.current.id,
      rtpCapabilities,
      producerId,
      kind
    }, async (params: any) => {
      if (params.error) {
        consumedProducersRef.current.delete(producerId);
        return;
      }

      try {
        const consumer = await recvTransportRef.current!.consume(params);
        const stream = new MediaStream([consumer.track]);

        consumer.track.onended = () => {
          setStudentStreams(prev => prev.filter(s => s.id !== studentSocketId));
          consumedProducersRef.current.delete(producerId);
        };

        if (kind === 'audio') {
          if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = stream;
            remoteAudioRef.current.play().catch(e => console.warn("Remote audio play blocked", e));
          }
        }

        if (kind === 'video') {
          setStudentStreams(prev => {
            const studentUserId = appData?.userId ? String(appData.userId).trim() : null;
            const cleanList = prev.filter(s => {
              return !(studentUserId && String(s.userId).trim() === studentUserId) && s.id !== studentSocketId;
            });
            return [...cleanList, { id: studentSocketId, userId: studentUserId || `guest-${studentSocketId}`, stream, kind: 'video' }];
          });

          if (isLowBandwidthMode) socket.emit('pause-consumer', { roomId: currentRoomId, consumerId: consumer.id });
        }

        socket.emit('resume-consumer', { roomId: currentRoomId, consumerId: consumer.id });
      } catch (err) {
        consumedProducersRef.current.delete(producerId);
      }
    });
  }, [socket, isLowBandwidthMode]);

  useEffect(() => {
    if (!socket) return;
    socket.on('student-left', ({ socketId, userId }: { socketId: string, userId: string }) => {
      setStudentStreams(prev => prev.filter(s => !(s.id === socketId || (userId && s.userId === userId))));
    });
    return () => { socket.off('student-left'); };
  }, [socket]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleTouch = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();
      const touch = e.touches[0];
      const rect = canvas.getBoundingClientRect();
      const x = (touch.clientX - rect.left) / rect.width;
      const y = (touch.clientY - rect.top) / rect.height;

      if (e.type === 'touchstart') {
        setIsDrawing(true);
        draw(x, y, false);
      } else if (e.type === 'touchmove') {
        if (tool === 'laser') {
          setLaserPos({ x, y, active: true });
          socket.emit('laser-move', { roomId, x, y, active: true });
        }
        if (isDrawing) draw(x, y, true);
      }
    };

    const handleTouchEnd = () => {
      setIsDrawing(false);
      lastXRef.current = null;
      lastYRef.current = null;
      if (tool === 'laser') {
        socket.emit('laser-move', { roomId, active: false });
        setLaserPos(p => ({ ...p, active: false }));
      }
    };

    canvas.addEventListener('touchstart', handleTouch, { passive: false });
    canvas.addEventListener('touchmove', handleTouch, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('touchstart', handleTouch);
      canvas.removeEventListener('touchmove', handleTouch);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDrawing, tool, socket, roomId]);

  const handleInitialize = useCallback(async (manualId?: string) => {
    const targetId = manualId || roomId;
    if (!targetId || isInitializing.current) return;
    isInitializing.current = true;

    try {
      const resp = await joinMediasoupRoom(targetId, 'TUTOR', user?.id || '');

      if (!deviceRef.current) {
        const dev = new mediasoupClient.Device();
        await dev.load({ routerRtpCapabilities: resp.routerRtpCapabilities });
        deviceRef.current = dev;
      }

      socket.emit('create-transport', { roomId: targetId, direction: 'recv' }, async (p: any) => {
        if (p.error) { isInitializing.current = false; return; }

        const transport = deviceRef.current.createRecvTransport({
          ...p,
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            { urls: 'stun:atrons.com.et:3478' },
            {
              urls: 'turns:atrons.com.et:5349?transport=tcp',
              username: 'atrons',
              credential: 'Myatrons@com1'
            }
          ],
        });

        transport.on('connect', ({ dtlsParameters }: any, cb: any, err: any) => {
          socket.emit('transport-connect', { transportId: transport.id, dtlsParameters, roomId: targetId }, (res: any) => res?.error ? err(res.error) : cb());
        });

        recvTransportRef.current = transport;
        setRoomId(targetId);
        setIsConnected(true);
        isInitializing.current = false;

        socket.emit('join-whiteboard', { roomId: targetId, page: 1 });

        if (resp.existingProducers && resp.existingProducers.length > 0) {
          queueMicrotask(() => {
            resp.existingProducers.forEach((ep: any) => {
              consumeStudentStream(ep.producerId, ep.kind, ep.socketId, ep.appData, targetId);
            });
          });
        }
      });
    } catch (err) { isInitializing.current = false; }
  }, [roomId, user?.id, consumeStudentStream]);

  const handleAutoStart = async (studentId: string, jobId: string) => {
    if (isStreaming.current) return;
    try {
      const currentJob = jobs.find((j: any) => j.id === jobId);
      if (!currentJob) return alert("Error: Job details not found in dashboard.");

      const assignedRate = Number(currentJob.budget || currentJob.hourlyRate || 25);
      const res = await api.post('/sessions/start', { studentId, jobId, hourlyRate: assignedRate });

      const { roomId: newId } = res.data;
      setRoomId(newId);
      setIsConnected(true);

      socket.emit('notify-student-session-start', { studentId, roomId: newId });
      await handleInitialize(newId);
    } catch (err: any) { alert(err.response?.data?.message || "Session start failed."); }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const studentId = params.get('studentId');
    const jobId = params.get('jobId');
    if (studentId && jobId && !isConnected) handleAutoStart(studentId, jobId);
  }, [jobs]);

  const getNormalizedCoords = (e: any) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) / rect.width, y: (clientY - rect.top) / rect.height };
  };

  const draw = (xP: number, yP: number, isDragging: boolean) => {
    if (tool === 'laser') return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const x = xP * (canvas.width / dpr);
    const y = yP * (canvas.height / dpr);

    const isEraser = tool === 'eraser';
    ctx.globalAlpha = tool === 'highlighter' ? 0.35 : 1.0;
    ctx.strokeStyle = isEraser ? '#ffffff' : (tool === 'highlighter' ? "#ffeb3b" : color);
    ctx.lineWidth = isEraser ? 40 : (tool === 'highlighter' ? 30 : lineWidth);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const pathData = { x: xP, y: yP, color: ctx.strokeStyle, width: ctx.lineWidth, alpha: ctx.globalAlpha, isDragging };
    strokesRef.current.push(pathData);

    if (!isDragging) {
      ctx.beginPath();
      ctx.moveTo(x, y);
      lastXRef.current = x;
      lastYRef.current = y;
    } else {
      if (lastXRef.current !== null && lastYRef.current !== null) {
        const midX = (lastXRef.current + x) / 2;
        const midY = (lastYRef.current + y) / 2;
        ctx.beginPath();
        ctx.moveTo(lastXRef.current, lastYRef.current);
        ctx.quadraticCurveTo(midX, midY, x, y);
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x, y);
        ctx.stroke();
      }
      lastXRef.current = x;
      lastYRef.current = y;
    }

    socket.emit('draw-data', { roomId, page: currentPage, pathData });
  };

  const handleClearBoard = () => {
    if (!window.confirm("Clear the current page?")) return;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      lastImgRef.current = null;
      strokesRef.current = [];
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const dpr = window.devicePixelRatio || 1;
      ctx.scale(dpr, dpr);
      socket.emit('clear-whiteboard', { roomId, page: currentPage });
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const imgData = ev.target?.result as string;
      const img = new Image();
      img.onload = () => {
        if (renderMapRef.current) {
          lastImgRef.current = img;
          renderMapRef.current(img);
          socket.emit('sync-background', { roomId, page: currentPage, image: imgData });
        }
      };
      img.src = imgData;
    };
    reader.readAsDataURL(file);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    setCurrentPage(newPage);
    if (newPage > totalPages) setTotalPages(newPage);

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      lastImgRef.current = null;
      strokesRef.current = [];
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const dpr = window.devicePixelRatio || 1;
      ctx.scale(dpr, dpr);
    }

    lastXRef.current = null;
    lastYRef.current = null;

    socket.emit('join-whiteboard', { roomId, page: newPage });
    socket.emit('change-page', { roomId, page: newPage });
  };

  useEffect(() => {
    if (!socket) return;

    const onQuizAnswer = (data: { studentName: string; answer: string }) => {
      setMessages(prev => [...prev, { sender: "SYSTEM", text: `🎓 ${data.studentName} answered: ${data.answer}`, type: 'other' }]);
    };

    const onDrawData = ({ page, pathData }: any) => {
      if (page !== currentPage) return;

      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      const dpr = window.devicePixelRatio || 1;

      strokesRef.current.push(pathData);

      const x = pathData.x * (canvas.width / dpr);
      const y = pathData.y * (canvas.height / dpr);
      ctx.globalAlpha = pathData.alpha || 1;
      ctx.strokeStyle = pathData.color;
      ctx.lineWidth = pathData.width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (!pathData.isDragging) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        lastXRef.current = x;
        lastYRef.current = y;
      } else {
        if (lastXRef.current !== null && lastYRef.current !== null) {
          const prevX = lastXRef.current;
          const prevY = lastYRef.current;
          const midX = (prevX + x) / 2;
          const midY = (prevY + y) / 2;

          ctx.quadraticCurveTo(prevX, prevY, midX, midY);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(midX, midY);
        } else {
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x, y);
          ctx.stroke();
        }
        lastXRef.current = x;
        lastYRef.current = y;
      }
    };

    const onSyncFull = ({ page, image }: { page: number, image: string }) => {
      if (page !== currentPage) return;

      const img = new Image();
      img.onload = () => {
        lastImgRef.current = img;
        if (renderMapRef.current) renderMapRef.current(img);
      };
      img.src = image;
    };

    const onWhiteboardHistory = (history: any[]) => {
      strokesRef.current = history || [];
      setupCanvas();
    };

    const onPageChanged = ({ page, history, background }: any) => {
      setCurrentPage(page);
      if (page > totalPages) setTotalPages(page);

      strokesRef.current = history || [];

      if (background) {
        const img = new Image();
        img.onload = () => {
          lastImgRef.current = img;
          setupCanvas();
        };
        img.src = background;
      } else {
        lastImgRef.current = null;
        setupCanvas();
      }
    };

    const onSessionEnded = () => {
      alert("Session closed by system.");
      window.location.href = '/tutor/home';
    };

    const onNewProducer = (d: any) => {
      if (d.socketId === socket.id) return;
      consumeStudentStream(d.producerId, d.kind, d.socketId, d.appData, roomIdRef.current);
    };

    socket.on('draw-data', onDrawData);
    socket.on('new-producer', onNewProducer);
    socket.on('sync-background', onSyncFull);
    socket.on('session-ended', onSessionEnded);
    socket.on('quiz-answer-received', onQuizAnswer);
    socket.on('whiteboard-history', onWhiteboardHistory);
    socket.on('page-changed', onPageChanged);

    return () => {
      socket.off('draw-data', onDrawData);
      socket.off('new-producer', onNewProducer);
      socket.off('sync-background', onSyncFull);
      socket.off('session-ended', onSessionEnded);
      socket.off('quiz-answer-received', onQuizAnswer);
      socket.off('whiteboard-history', onWhiteboardHistory);
      socket.off('page-changed', onPageChanged);
    };
  }, [socket, currentPage, totalPages, setupCanvas, consumeStudentStream]);

  useEffect(() => {
    if (!socket) return;
    const handleChat = (data: { sender: string; text: string; timestamp?: number }) => {
      const messageFingerprint = `${data.sender}:${data.text}`;
      if (lastProcessedMessageRef.current === messageFingerprint) return;

      const isMe = data.sender.trim().toLowerCase() === user?.fullName?.trim().toLowerCase();
      if (isMe) return;

      lastProcessedMessageRef.current = messageFingerprint;

      setMessages(prev => {
        const isDuplicate = prev.length > 0 && prev[prev.length - 1].text === data.text && prev[prev.length - 1].sender === data.sender;
        if (isDuplicate) return prev;
        return [...prev, { ...data, type: 'other' }];
      });
    };

    socket.off('receive-chat', handleChat);
    socket.on('receive-chat', handleChat);
    return () => { socket.off('receive-chat', handleChat); };
  }, [socket, user?.fullName]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlRoomId = params.get('room');
    if (urlRoomId && !isConnected && !mediaSoupActive.current) {
      mediaSoupActive.current = true;
      handleInitialize(urlRoomId);
    }
  }, [isConnected, handleInitialize]);

  const startStream = async () => {
    if (isStreaming.current || !deviceRef.current) return;
    isStreaming.current = true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: { channelCount: 1, sampleRate: 48000, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });

      localStreamRef.current = stream;
      if (localVideoRef.current) { localVideoRef.current.srcObject = stream; }

      socket.emit('create-transport', { roomId, direction: 'send' }, async (p: any) => {
        if (p.error) { isStreaming.current = false; return; }

        const transport = deviceRef.current.createSendTransport({
          ...p,
          iceServers: [
            { urls: 'stun:stun.l.google.com:19302' },
            { urls: 'stun:stun1.l.google.com:19302' },
            { urls: 'stun:atrons.com.et:3478' },
            {
              urls: 'turns:atrons.com.et:5349?transport=tcp',
              username: 'atrons',
              credential: 'Myatrons@com1'
            }
          ],
        });
        sendTransportRef.current = transport;

        transport.on('connect', ({ dtlsParameters }: any, cb: any, err: any) => {
          socket.emit('transport-connect', { transportId: transport.id, dtlsParameters, roomId }, (res: any) => res?.error ? err(res.error) : cb());
        });

        transport.on('produce', ({ kind, rtpParameters, appData }: any, cb: any, err: any) => {
          socket.emit('transport-produce', { transportId: transport.id, kind, rtpParameters, roomId, appData: { ...appData, kind } }, (res: any) => res.error ? err(res.error) : cb({ id: res.id }));
        });

        try {
          for (const track of stream.getTracks()) {
            const producer = await transport.produce({
              track,
              appData: { kind: track.kind, userId: user?.id, socketId: socket.id },
              encodings: track.kind === 'video'
                ? [{ maxBitrate: 600000, priority: 'high' }]
                : [{ maxBitrate: 64000, priority: 'high' }],
              codecOptions: track.kind === 'audio' ? { opusDtx: true, opusStereo: false } : undefined
            });

            if (track.kind === 'video') { videoProducerRef.current = producer; }
          }
          setIsLocalStreaming(true);
          setIsConnected(true);
        } catch (produceErr) { } finally { isStreaming.current = false; }
      });
    } catch (err) { isStreaming.current = false; alert("Could not access camera."); }
  };

  useEffect(() => {
    if (localVideoRef.current && localStreamRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
      localVideoRef.current.muted = true;
      localVideoRef.current.play().catch(err => { });
    }
  }, [isLocalStreaming]);

  const sendChat = () => {
    if (!chatInput.trim()) return;
    socket.emit('send-chat', { roomId, text: chatInput, sender: user?.fullName || 'Tutor' });
    setMessages(p => [...p, { sender: 'Me', text: chatInput, type: 'me' }]);
    setChatInput('');
  };

  const launchQuiz = () => {
    if (!quizData.question) return;
    const payload = { roomId, type: quizData.type, options: quizData.type === 'mcq' ? quizData.options.filter(o => o.trim() !== '') : [] };
    socket.emit('broadcast-quiz', payload);
    setShowQuizModal(false);
    setQuizData({ type: 'mcq', question: '', options: ['', '', '', ''] });
  };

  const handleEndSession = async () => {
    const params = new URLSearchParams(window.location.search);
    const actualId = roomId || params.get('room');

    if (!actualId || !window.confirm("End session?")) return;

    try {
      await api.post('/sessions/end', { roomId: actualId });
      setIsConnected(false);
      window.location.replace('/tutor/home');
    } catch (err) {
      alert("Failed to end session.");
    }
  };

  return (
    <div className="h-[100dvh] w-full flex flex-col bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 overflow-hidden relative">
      {!isFocusMode && (
        <header className="h-16 shrink-0 bg-slate-900/80 backdrop-blur-md border-b border-slate-800 px-4 lg:px-6 flex items-center justify-between z-30 shadow-2xl">
          <div className="flex items-center gap-4 lg:gap-8">
            <Link to="/tutor/home" className="flex items-center gap-3 cursor-pointer">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center font-black italic shadow-lg shadow-blue-600/20 text-white hover:scale-105 transition-transform">T</div>
              <div className="hidden sm:block"><span className="font-black text-[11px] block leading-none italic uppercase tracking-tighter text-white">AtronsTutor <span className="text-blue-500">Console</span></span></div>
            </Link>
            <nav className="flex items-center gap-1 sm:gap-2 border-l border-slate-800 pl-4 sm:pl-6">
              <Link to="/tutor/home" className={`text-[10px] font-black uppercase flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${window.location.pathname === '/tutor/home' ? 'text-blue-400 bg-blue-500/10' : 'text-slate-400 hover:text-white'}`}><LayoutDashboard size={14} /> <span className="hidden md:block">{t('home_nav', 'Home')}</span></Link>
              <Link to="/tutor/jobs/browse" className={`text-[10px] font-black uppercase flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${window.location.pathname === '/tutor/jobs/browse' ? 'text-blue-400 bg-blue-500/10' : 'text-slate-400 hover:text-white'}`}><Search size={14} /> <span className="hidden md:block">{t('browse_nav', 'Browse')}</span></Link>
              <Link to="/tutor/my-classes" className={`text-[10px] font-black uppercase flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${window.location.pathname === '/tutor/my-classes' ? 'text-blue-400 bg-blue-500/10' : 'text-slate-400 hover:text-white'}`}><Users size={14} /> <span className="hidden md:block">{t('classes_nav', 'Classes')}</span></Link>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={() => setIsLowBandwidthMode(!isLowBandwidthMode)} title={isLowBandwidthMode ? "Switch to High Quality Mode" : "Engage Data-Saving Mode"} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-[10px] font-black uppercase transition-all border ${isLowBandwidthMode ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'}`}>
              {isLowBandwidthMode ? <WifiOff size={14} /> : <Wifi size={14} />}
              <span className="hidden sm:inline">{isLowBandwidthMode ? t('data_saver_on', "Data Saver On") : t('optimize_network', "Optimize Network")}</span>
            </button>

            <div className="hidden lg:flex bg-slate-950 pl-4 pr-2 py-1.5 rounded-2xl border border-slate-800 items-center justify-center gap-3">
              <span className="text-xs font-black text-white">{Number(user?.walletBalance || 0).toFixed(2)} <span className="ml-1 text-[10px] text-slate-500">ETB</span></span>
              <button onClick={() => setIsWithdrawOpen(true)} className="bg-blue-600/20 text-blue-400 hover:bg-blue-600 hover:text-white px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all">
                {t('payout', 'Payout')}
              </button>
            </div>

            <button onClick={() => window.confirm(t('confirm_logout', "Are you sure you want to logout?")) && logout()} className="p-2.5 text-slate-500 bg-slate-800 rounded-xl hover:bg-red-500/20 hover:text-red-500 transition-all border border-slate-700"><LogOut size={16} /></button>
          </div>
        </header>
      )}

      {/* Main Workspace Frame */}
      <main className="flex-1 p-2 lg:p-3 flex flex-col lg:flex-row gap-3 overflow-y-auto lg:overflow-hidden relative">

        {/* Toolbar */}
        <aside className="fixed bottom-4 left-1/2 -translate-x-1/2 lg:relative lg:left-0 lg:translate-x-0 w-[92%] lg:w-16 h-16 lg:h-auto bg-slate-800/95 backdrop-blur-lg rounded-2xl flex flex-row lg:flex-col items-center justify-around lg:py-6 lg:gap-6 border border-slate-700 shadow-2xl z-[40] shrink-0">
          <button title="Pencil Tool" onClick={() => setTool('pen')} className={`p-2.5 lg:p-3 rounded-xl transition-all ${tool === 'pen' ? 'bg-blue-600 shadow-lg scale-110' : 'text-slate-500 hover:bg-slate-700'}`} > <Pencil size={20} /> </button>
          <button title="Highlighter" onClick={() => setTool('highlighter')} className={`p-3 rounded-xl transition-all ${tool === 'highlighter' ? 'bg-yellow-500 text-slate-900 shadow-lg' : 'text-slate-500 hover:bg-slate-700'}`}> <Highlighter size={20} /> </button>
          <button title="Laser Pointer" onClick={() => setTool('laser')} className={`p-2.5 lg:p-3 rounded-xl transition-all ${tool === 'laser' ? 'bg-red-500 shadow-lg' : 'text-slate-500 hover:bg-slate-700'}`} >  <MousePointer2 size={20} /> </button>
          <button title="Eraser Tool" onClick={() => setTool('eraser')} className={`p-2.5 lg:p-3 rounded-xl transition-all ${tool === 'eraser' ? 'bg-slate-200 text-slate-900 shadow-lg' : 'text-slate-500 hover:bg-slate-700'}`}> <Eraser size={20} /> </button>
          <button
            title={isStudentDrawingAllowed ? "Lock Student Drawing" : "Allow Student Drawing"}
            onClick={toggleStudentDrawing}
            className={`p-2.5 lg:p-3 rounded-xl transition-all ${isStudentDrawingAllowed ? 'bg-emerald-500 text-white shadow-lg' : 'text-slate-500 hover:bg-slate-700'}`}
          >
            {isStudentDrawingAllowed ? <Unlock size={20} /> : <LockIcon size={20} />}
          </button>
          <div className="hidden lg:block h-[2px] w-8 bg-slate-700 rounded-full" />
          <button title="Upload Image/Slide" onClick={() => fileInputRef.current?.click()} className="p-2.5 lg:p-3 text-blue-400 hover:bg-slate-700 rounded-xl transition-all" >  <ImageIcon size={20} />  </button>
          <button title="Launch Quiz" onClick={() => setShowQuizModal(true)} className="p-2.5 lg:p-3 text-purple-400 hover:bg-slate-700 rounded-xl transition-all" > <HelpCircle size={20} /> </button>
          <button title="Clear Whiteboard" onClick={handleClearBoard} className="p-2.5 lg:p-3 text-slate-500 hover:text-red-500 transition-all" >  <Trash2 size={20} />  </button>
          <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={handleFileUpload} />
          <button title="End Session" onClick={handleEndSession} className={`p-2.5 lg:p-3 text-red-500 lg:mt-auto hover:bg-red-500/10 rounded-xl transition-all ${isConnected ? 'animate-pulse' : ''}`} >  <LogOut size={20} /> </button>
        </aside>

        {/* Whiteboard */}
        <section className="min-h-[50vh] shrink-0 lg:h-full lg:flex-1 bg-white rounded-[1.5rem] lg:rounded-[2.5rem] relative overflow-hidden border-2 lg:border-4 border-slate-800 shadow-inner group min-w-0">
          <div className="absolute top-4 left-4 lg:top-6 lg:left-6 z-20 flex gap-2 lg:gap-4 bg-white/95 backdrop-blur px-3 lg:px-4 py-2 lg:py-2.5 rounded-xl lg:rounded-2xl border shadow-xl items-center">
            <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-6 h-6 lg:w-8 lg:h-8 rounded-lg cursor-pointer ring-2 ring-slate-100 bg-transparent" />
            <div className="w-[1px] h-6 bg-slate-200 mx-1" />
            <div className="flex items-center gap-2">
              <input type="range" min="1" max="25" value={lineWidth} onChange={e => setLineWidth(parseInt(e.target.value))} className="w-16 lg:w-24 accent-blue-600" />
              <span className="text-[9px] lg:text-[10px] font-black text-slate-400 w-8 uppercase tracking-tighter">{lineWidth}px</span>
            </div>
          </div>

          <button onClick={() => { setIsFocusMode(!isFocusMode); setTimeout(setupCanvas, 150); }} className="absolute top-4 right-4 lg:top-6 lg:right-6 z-20 p-2 lg:p-3 bg-slate-800/90 rounded-xl lg:rounded-2xl text-white shadow-xl">
            {isFocusMode ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>

          {/* PAGE NAVIGATION CONTROLS */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3 bg-white/95 backdrop-blur px-4 py-2 rounded-2xl border shadow-xl">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg disabled:opacity-30 transition-all"
              title="Previous Page"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest min-w-[70px] text-center">
              Pg {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-all"
              title={currentPage < totalPages ? "Next Page" : "Add New Page"}
            >
              {currentPage < totalPages ? <ChevronRight size={20} /> : <Plus size={20} />}
            </button>
          </div>
          <div
            ref={containerRef}
            className="w-full h-full bg-slate-50 relative overflow-hidden"
            style={{
              cursor: tool === 'laser'
                ? 'none'
                : tool === 'pen'
                  ? `url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z'/%3E%3C/svg%3E") 2 22, crosshair`
                  : tool === 'eraser'
                    ? `url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='white' stroke='black' stroke-width='2'%3E%3Crect x='2' y='2' width='20' height='20' rx='4'/%3E%3C/svg%3E") 12 12, crosshair`
                    : 'crosshair'
            }}
          >
            <canvas
              ref={canvasRef}
              className="block touch-none"
              onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); setIsDrawing(true); const { x, y } = getNormalizedCoords(e); draw(x, y, false); }}
              onPointerMove={(e) => {
                const { x, y } = getNormalizedCoords(e);
                if (tool === 'laser') { setLaserPos({ x, y, active: true }); socket.emit('laser-move', { roomId, x, y, active: true }); }
                if (isDrawing) draw(x, y, true);
              }}
              onPointerUp={(e) => { setIsDrawing(false); lastXRef.current = null; lastYRef.current = null; e.currentTarget.releasePointerCapture(e.pointerId); if (tool === 'laser') { socket.emit('laser-move', { roomId, active: false }); setLaserPos(p => ({ ...p, active: false })); } }}
              onPointerCancel={(e) => { setIsDrawing(false); lastXRef.current = null; lastYRef.current = null; e.currentTarget.releasePointerCapture(e.pointerId); }}
            />
            {tool === 'laser' && laserPos.active && (
              <div className="absolute w-4 h-4 bg-red-500 rounded-full shadow-[0_0_20px_red] animate-ping pointer-events-none z-50" style={{ left: `${laserPos.x * 100}%`, top: `${laserPos.y * 100}%`, transform: 'translate(-50%, -50%)' }} />
            )}
          </div>
        </section>

        {/* Video & Chat Sidepane */}
        {!isFocusMode && (
          <aside className="flex flex-col gap-3 shrink-0 lg:w-[340px] lg:h-full lg:overflow-y-auto pb-24 lg:pb-0 min-h-0 custom-scrollbar">

            {/* 🛠️ FIX: Video Group wrapped in shrink-0 so they NEVER get crushed */}
            <div className="flex flex-col gap-3 shrink-0">

              {/* Local Video */}
              <div className="bg-slate-950 rounded-2xl lg:rounded-[2rem] aspect-video relative overflow-hidden border-2 border-slate-700 shadow-2xl shrink-0">
                <video ref={localVideoRef} autoPlay muted playsInline className={`w-full h-full object-cover transition-all ${isLowBandwidthMode ? 'brightness-50 blur-sm pointer-events-none' : ''}`} />

                {isLowBandwidthMode && isLocalStreaming && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-4 bg-black/40 text-center">
                    <span className="text-[10px] font-black tracking-widest text-amber-400 uppercase bg-slate-900/90 border border-amber-500/30 px-3 py-1.5 rounded-xl backdrop-blur-md">{t('outbound_paused', 'Outbound Video Paused')}</span>
                  </div>
                )}

                {!isLocalStreaming && (
                  <div className="camera-overlay absolute inset-0 flex flex-col items-center justify-center bg-slate-900/80 backdrop-blur-sm">
                    <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center mb-3"><Camera size={18} className="text-slate-500" /></div>
                    <button onClick={startStream} className="bg-blue-600 text-[10px] font-black uppercase px-8 py-3 rounded-2xl flex items-center gap-2 shadow-2xl active:scale-95 transition-all"><div className="w-2 h-2 rounded-full bg-white animate-pulse" /> {t('go_live', 'Go Live')}</button>
                  </div>
                )}

                {isLocalStreaming && !isLowBandwidthMode && (
                  <div className="absolute top-4 left-4 bg-red-600 px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest shadow-lg flex items-center gap-2 border border-white/20"><div className="w-1.5 h-1.5 rounded-full bg-white animate-live" /> {t('live', 'Live')}</div>
                )}
              </div>

              {/* 🚀 STUDENT VIDEO CONTAINER */}
              <div className="flex lg:flex-col gap-2 overflow-x-auto no-scrollbar shrink-0 py-1">
                {studentStreams.map((s) => (
                  <div key={s.userId} className="w-[180px] lg:w-full shrink-0 relative rounded-xl lg:rounded-3xl overflow-hidden border-2 border-slate-800 aspect-video shadow-md bg-slate-950">
                    <div className="w-full h-full flex items-center justify-center overflow-hidden">
                      <StudentVideo
                        stream={s.stream}
                        volume={0.8}
                        className="min-w-full min-h-full object-cover"
                        t={t}
                        onScan={() => {
                          socket.emit('request-homework-scan', { roomId, targetSocketId: s.id });
                        }}
                      />
                    </div>
                    <div className="absolute top-2 left-2 bg-black/40 backdrop-blur-md px-2 py-0.5 rounded-md border border-white/10">
                      <span className="text-[8px] font-black uppercase text-white tracking-widest"> {t('student', 'Student')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 🛠️ FIX: Chat Container now uses lg:flex-1 to gracefully fill remaining space */}
            <div className="lg:flex-1 bg-slate-900 border border-slate-800 rounded-2xl lg:rounded-[2.5rem] flex flex-col overflow-hidden shadow-2xl shrink-0 min-h-[300px] mb-4 mt-auto">
              <div className="px-5 py-4 border-b border-slate-800 text-[10px] font-black text-blue-400 uppercase tracking-widest flex justify-between items-center bg-slate-950/30">
                <span>{t('class_chat', 'Class Chat')}</span>
                <div className="flex gap-1"><div className="w-1 h-1 rounded-full bg-blue-500/50" /><div className="w-1 h-1 rounded-full bg-blue-500" /></div>
              </div>
              <div className="flex-1 p-4 overflow-y-auto flex flex-col gap-3 text-[11px] custom-scrollbar bg-slate-950/10">
                {messages.map((m, i) => (
                  <div key={i} className={`flex flex-col ${m.type === 'me' ? 'items-end' : 'items-start'}`}>
                    <span className="text-[7px] text-slate-600 uppercase font-black mb-1 ml-1 tracking-tighter">{m.sender}</span>
                    <div className={`px-4 py-2 rounded-2xl max-w-[85%] shadow-sm ${m.type === 'me' ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-slate-800 text-slate-200 rounded-tl-none border border-white/5'}`}>{m.text}</div>
                  </div>
                ))}
              </div>
              <div className="p-2 bg-slate-950/40 border-t border-slate-800 flex gap-2 m-1 rounded-2xl mb-8 lg:mb-1">
                <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && sendChat()} placeholder={t('type_message', 'Type message...')} className="flex-1 bg-transparent px-3 py-2 text-[11px] outline-none text-white placeholder:text-slate-600" />
                <button onClick={sendChat} className="p-3 bg-blue-600 text-white rounded-xl active:scale-90 hover:bg-blue-500 transition-all shadow-lg"><Send size={14} /></button>
              </div>
            </div>
          </aside>
        )}
      </main>

      {
        showQuizModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-950/90 backdrop-blur-md">
            <div className="bg-white rounded-[2.5rem] w-full max-w-sm p-10 text-slate-900 shadow-2xl border-4 border-slate-800">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black uppercase italic tracking-tighter text-blue-600">{t('broadcast_quiz', 'Broadcast Quiz')}</h3>
                <button onClick={() => setShowQuizModal(false)} className="text-slate-400 hover:text-slate-900"><X size={20} /></button>
              </div>
              <div className="flex bg-slate-100 p-1 rounded-xl mb-6">
                <button onClick={() => setQuizData({ ...quizData, type: 'mcq' })} className={`flex-1 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${quizData.type === 'mcq' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500'}`}>{t('multiple_choice', 'Multiple Choice')}</button>
                <button onClick={() => setQuizData({ ...quizData, type: 'text' })} className={`flex-1 py-2 rounded-lg text-[10px] font-black uppercase transition-all ${quizData.type === 'text' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500'}`}>{t('answer_question', 'Answer Question')}</button>
              </div>
              <textarea value={quizData.question} onChange={e => setQuizData({ ...quizData, question: e.target.value })} className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-2xl font-bold mb-4 text-sm resize-none outline-none focus:border-blue-500" placeholder={t('what_is_question', 'What is your question?')} />
              {quizData.type === 'mcq' ? (
                <div className="grid grid-cols-2 gap-2 mb-8">
                  {quizData.options.map((opt, i) => (
                    <input key={i} value={opt} onChange={e => { const n = [...quizData.options]; n[i] = e.target.value; setQuizData({ ...quizData, options: n }); }} className="bg-slate-50 border border-slate-200 p-3 rounded-xl text-[10px] font-bold outline-none focus:border-blue-400" placeholder={`Option ${i + 1}`} />
                  ))}
                </div>
              ) : (
                <div className="mb-8 p-4 bg-blue-50 border-2 border-dashed border-blue-100 rounded-2xl text-center"><p className="text-[10px] font-black text-blue-400 uppercase">{t('student_text_answer_box', 'Student text answer box')}</p></div>
              )}
              <button onClick={launchQuiz} className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black uppercase tracking-widest shadow-xl hover:bg-blue-500 transition-all">{t('send_to_student', 'Send to Student')}</button>
            </div>
          </div>
        )
      }

      <button onClick={() => navigate('/tutor/home')} className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-500 text-white p-4 rounded-full shadow-2xl shadow-blue-500/30 transition-all hover:-translate-y-1 z-50 group flex items-center gap-0 overflow-hidden">
        <Home size={24} />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-3 transition-all duration-300 ease-in-out whitespace-nowrap font-bold text-sm uppercase tracking-wider">{t('dashboard', 'Dashboard')}</span>
      </button>

      <DepositModal isOpen={isDepositOpen} onClose={() => setIsDepositOpen(false)} onSuccess={() => { }} />

      <WithdrawModal
        isOpen={isWithdrawOpen}
        onClose={() => setIsWithdrawOpen(false)}
        currentBalance={Number(user?.walletBalance || 0)}
        onSuccess={() => window.location.reload()}
      />
      <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />
    </div >
  );
};

export default TutorDashboard;